import { Component, Suspense } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { LoadingSpinner } from './LoadingSpinner.js';

interface LazyRouteProps {
  children: ReactNode;
}

/**
 * Wraps a lazy-loaded route with a Suspense fallback and an error boundary.
 * Chunk-load failures (tab outlived a deploy, so the old hashed chunk is
 * gone from CloudFront) get a targeted message because the fix is a
 * reload. Other render errors get a generic fallback so we don't mislead
 * the user about the cause.
 */
export function LazyRoute({ children }: LazyRouteProps): React.JSX.Element {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

interface BoundaryState {
  error: Error | null;
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message)
  );
}

class ChunkErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- surfaces lazy-route failures to ops
    console.error('LazyRoute render failure:', error, info);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const message = isChunkLoadError(error)
      ? "Couldn't load this page — it may have been updated since your tab opened."
      : 'Something went wrong loading this page. Reloading may help.';
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-sm text-gray-600">{message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand/90"
        >
          Reload
        </button>
      </div>
    );
  }
}
